// G360_K505 - credential_status: the assistant can answer credential questions.
// metricRegistry.js - the metrics the assistant is allowed to answer from.
//
// WHY THIS EXISTS
//
// Raw text-to-SQL over this schema would not work and would fail in the
// worst way. On enterprise-scale schemas the best models solve well under
// a quarter of tasks, and the dominant failure is not an error - it is a
// confident wrong number from a wrong join or the wrong aggregation grain.
// On a page that tells a CISO "94 agents act autonomously with no owner",
// a silently wrong figure is worse than no answer.
//
// So the assistant never writes SQL. It picks an entry from this list and
// supplies its parameters. The function behind each entry is the same one
// the pages already render, which means the assistant cannot disagree with
// the console - there is one definition of each number, not two.
//
// THE HONESTY CONTRACT IS ALREADY IN THE DATA
//
// These functions were built to say what they cannot see: measured_pct and
// unmeasured_drivers on the risk scores, a measured boolean per row on
// ai_exposure_risks, *_is_sample flags across the exposure report,
// what_we_see and the_limit on the coverage maps. `unmeasured` is declared
// per entry so the renderer can hold the line the SQL already holds: an
// unmeasured field is null, not a plausible default.
//
// RLS, AND WHY THIS IS A BROWSER MODULE
//
// Every rpc here runs through the console's own supabase client, as the
// signed-in user, under row-level security. It is deliberately NOT called
// from a Netlify function: _lib/auth.js builds its client from the service
// role, which bypasses RLS, and a routed query running with that key in a
// multi-tenant product is one bad parameter away from a cross-tenant read.
// The gateway receives rows that have already been filtered by the
// database. It never queries tenant data for this path.
//
// ADDING AN ENTRY
//
// Confirm the signature first - several of these functions have two
// overloads, one argument-less and one tenant-explicit, and the
// argument-less ones resolve through my_active_tenant(), which returns
// nothing where auth.uid() is null. Always register the tenant-explicit
// overload where one exists.

/* Field roles, so the renderer never has to guess:
     value       - a number or label that may be stated
     flag        - a boolean saying whether the value beside it was measured
     unmeasured  - a list of what could not be measured; name it, never zero it
     detail      - structured evidence for the drill-down, not for prose
*/

/* G360_K505 - the four credential states that were actually read from
   Entra. Anything else nhi_credential_status returns - not_measured, or a
   word it gains later - is unknown and is counted as unknown, never as
   clean. */
const CRED_READ = { expired: true, expiring: true, current: true, none_found: true };

/* G360_K506 - a month the model names ("2026-08") as the first day of that
   month, or null so the function's own default (this month) applies. A
   string that is not a month is dropped, never passed through. */
function monthStart(p) {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String((p && p.month) || '').trim());
  return m ? m[1] + '-' + m[2] + '-01' : null;
}
function withMonth(args, p) {
  const d = monthStart(p);
  if (d) args.p_period_start = d;
  return args;
}
// The dimensions token_attribution_coverage reads. Anything else falls back
// to cost_center inside the function, so it is not passed at all.
const ATTRIBUTION_DIMS = { cost_center: 1, business_unit: 1, team: 1, environment: 1, agent: 1, app: 1 };
const ACTING = (r) => r.autonomy_tier === 'A3' || r.autonomy_tier === 'A4';

export const METRICS = [
  {
    key: 'exposure_score',
    rpc: 'ai_exposure_score',
    args: (t) => ({ p_tenant: t }),
    title: 'AI Exposure Score',
    description:
      'The composite exposure score with its band, confidence, per-dimension breakdown and decay. '
      + 'Dimensions are aggregated with a geometric mean so a strong dimension cannot offset a failed one, '
      + 'and the band is capped by the weakest MEASURED dimension.',
    examples: [
      'what is our AI exposure score',
      'why is our exposure band high',
      'which dimension is dragging the score down',
    ],
    value: ['score', 'band', 'confidence', 'model_version', 'computed_at'],
    detail: ['dimensions', 'findings', 'contributors', 'decay'],
    // The score is never quotable without its confidence: a high score over
    // thin evidence is not the same claim as a high score over full evidence.
    alwaysWith: ['confidence'],
  },

  {
    key: 'exposure_confidence',
    rpc: 'exposure_confidence',
    args: (t) => ({ p_tenant: t }),
    title: 'Exposure confidence',
    description:
      'How much of the estate the exposure score is actually computed from: connected sources, '
      + 'sources in scope, and stale sources, with a per-source breakdown.',
    examples: [
      'how confident is the exposure score',
      'how much of our estate is actually measured',
      'which sources are stale',
    ],
    value: ['confidence_pct', 'connected', 'in_scope', 'stale'],
    detail: ['breakdown'],
  },

  {
    key: 'exposure_decay',
    rpc: 'exposure_decay',
    args: (t) => ({ p_tenant: t }),
    title: 'Exposure decay',
    description: 'Per-dimension penalties currently applied to the score, each with its cause and remedy.',
    examples: [
      'why has our score dropped',
      'what is penalising the exposure score',
    ],
    value: ['dimension', 'penalty', 'cause', 'remedy'],
  },

  {
    key: 'exposure_risks',
    rpc: 'ai_exposure_risks',
    args: (t) => ({ p_tenant: t }),
    title: 'Top exposure risks',
    description:
      'Ranked risks with severity, the reason, the metric behind each, and whether that metric was measured. '
      + 'Rows are ordered by severity then points.',
    examples: [
      'what are our biggest AI risks',
      'what should we fix first',
      'which risks are actually measured',
    ],
    value: ['risk', 'level', 'why', 'metric', 'metric_label', 'route'],
    // A risk with measured=false has no number behind it. Say the risk,
    // never the figure.
    flag: ['measured'],
  },

  {
    key: 'exposure_report',
    rpc: 'ai_exposure_report',
    args: () => ({}),
    title: 'Exposure report headline figures',
    description:
      'The headline estate figures: AI systems, autonomous agents, machine identities, monthly spend, '
      + 'unapproved agents, shadow AI, compliance gaps, and how old the most recent AI activity is.',
    examples: [
      'how many AI systems do we run',
      'how many autonomous agents are there',
      'what is our monthly AI spend',
    ],
    value: [
      'ai_systems', 'agents_autonomous', 'agents_total', 'nhi_count', 'monthly_spend',
      'unapproved_agents', 'shadow_ai', 'high_risk_apps', 'compliance_gaps',
      'autonomous_decisions_30d', 'last_ai_activity_at', 'ai_activity_age_days', 'spend_window_days',
    ],
    // Every *_is_sample flag means the figure beside it is seeded demonstration
    // data. It must be labelled as such or withheld - never stated as the estate.
    flag: [
      'ai_systems_is_sample', 'agents_is_sample', 'spend_is_sample',
      'nhi_is_sample', 'decisions_is_sample',
    ],
    unmeasured: ['agents_autonomy_unknown', 'agents_status_unknown', 'agents_discovered_unreviewed'],
    // This overload takes no tenant and resolves through my_active_tenant().
    // Fine from the browser, where auth.uid() is set. It is one of the
    // argument-less functions still to be converted.
    tenantImplicit: true,
  },

  {
    key: 'agent_risk',
    rpc: 'agent_risk_score',
    args: (t) => ({ p_tenant: t }),
    title: 'Agent risk scores',
    description:
      'Per-agent risk score and band, with how much of it was measured, how many required owners are '
      + 'missing, the drivers behind the score, and the drivers that could not be measured.',
    examples: [
      'which agents are riskiest',
      'which agents have no owner',
      'why is that agent scored high',
    ],
    value: ['agent_name', 'platform', 'agent_status', 'lifecycle_state', 'risk_score', 'risk_band', 'owners_missing'],
    flag: ['measured_pct'],
    unmeasured: ['unmeasured_drivers'],
    detail: ['drivers'],
    // Below 50 percent measured, the standing product rule is that an agent is
    // neither safe nor unsafe - too little is known to rank it against others.
    caveat: 'An agent below 50 percent measured evidence is not ranked; say so rather than placing it.',
  },

  {
    key: 'tool_risk',
    rpc: 'tool_risk_score',
    args: (t) => ({ p_tenant: t }),
    title: 'Tool risk scores',
    description: 'Per-tool risk score and band with provenance, measured share, drivers and unmeasured drivers.',
    examples: [
      'which AI tools are riskiest',
      'what do we know about that vendor',
    ],
    value: ['tool_name', 'vendor', 'status', 'risk_score', 'risk_band', 'provenance'],
    flag: ['measured_pct'],
    unmeasured: ['unmeasured_drivers'],
    detail: ['drivers'],
  },

  {
    key: 'governance_score',
    rpc: 'governance_score',
    args: (t) => ({ p_tenant: t }),
    title: 'Governance score',
    description: 'The governance score with how many domains exist and how many of them were measured.',
    examples: ['what is our governance score', 'how many governance domains are measured'],
    value: ['score', 'measured_count', 'domain_count'],
    detail: ['domains'],
    alwaysWith: ['measured_count', 'domain_count'],
  },

  {
    key: 'posture_score',
    rpc: 'posture_score',
    args: (t) => ({ p_tenant: t }),
    title: 'Posture score',
    description:
      'Posture across discovery, shadow AI, policy, DLP, coverage and compliance, with the counts '
      + 'each component was computed from.',
    examples: ['what is our posture score', 'how many controls are satisfied', 'how much shadow AI is there'],
    value: [
      'score', 'discovery', 'shadow', 'policy', 'dlp', 'coverage', 'compliance',
      'tools_total', 'tools_shadow', 'members', 'members_covered', 'controls_total', 'controls_satisfied',
    ],
  },

  {
    key: 'asi_coverage',
    rpc: 'asi_coverage',
    args: (t) => ({ p_tenant: t }),
    title: 'OWASP Agentic Top 10 coverage',
    description:
      'Per risk code: what Govern360 can see, what it cannot, and how many findings exist. '
      + 'Codes report not_observed with a stated reason where the product has no visibility.',
    examples: [
      'are we covered for the OWASP agentic top 10',
      'what can Govern360 not see',
      'do we detect prompt injection',
    ],
    value: ['code', 'risk_name', 'status', 'finding_count', 'finding_label'],
    // the_limit is the honest boundary of the product. It is part of the
    // answer, not a footnote to be dropped for brevity.
    unmeasured: ['the_limit'],
    detail: ['what_we_see'],
    alwaysWith: ['the_limit'],
  },

  {
    key: 'exposure_paths', // g360-k376
    rpc: 'agent_exposure_paths',
    args: (t) => ({ p_tenant: t }),
    title: 'Exposure paths',
    description:
      'Named paths across the estate where exposure can travel between agents, each with a '
      + 'severity, a headline, the detail behind it, and how many agents are involved. '
      + 'Computed from configuration - it names where exposure COULD travel, never where it has.',
    examples: [
      'what exposure paths do we have',
      'how could an injection travel through our agents',
      'what is the worst exposure path',
    ],
    value: ['path_kind', 'severity', 'headline', 'detail', 'agents_involved'],
    detail: ['agent_ids'],
    caveat: 'These are derived from configuration, not from traffic. Say could, never did.',
  },

  {
    key: 'exposure_legs', // g360-k376
    rpc: 'agent_exposure_legs',
    args: (t) => ({ p_tenant: t }),
    title: 'Exposure legs per agent',
    description:
      'Per agent, which legs it has: takes untrusted input, reads private data, writes the '
      + 'shared substrate, sends externally - and whether it holds the full trifecta. '
      + 'An agent with every leg can carry untrusted content all the way out on its own.',
    examples: [
      'which agents have the full trifecta',
      'how many agents can take untrusted input and send outside',
      'which agents read private data and send externally',
    ],
    value: ['agent_name', 'environment_name', 'autonomy_tier', 'untrusted_in',
            'reads_private', 'writes_substrate', 'external_out', 'full_trifecta'],
    // A row per agent, each carrying an evidence blob. It stays here.
    detail: ['evidence', 'agent_id'],
  },

  {
    key: 'agent_signal', // g360-k376
    rpc: 'agent_exposure_signal',
    args: (t) => ({ p_tenant: t }),
    title: 'Agent exposure signal',
    description:
      'Per agent: exposure score, owner, autonomy, how many tools and MCP tools it can '
      + 'reach, how many of those are live, knowledge sources, channels, when it was last '
      + 'published, and the reasons and ASI codes behind its score.',
    examples: [
      'which agents have the most tool reach',
      'which agents have not been published recently',
      'why does that agent score the way it does',
    ],
    value: ['agent_name', 'environment', 'owner_name', 'autonomy', 'exposure_score',
            'shared_tenant', 'tool_count', 'mcp_tools', 'mcp_live', 'knowledge_count',
            'channel_count', 'last_published', 'days_since_publish'],
    detail: ['reasons', 'asi_codes', 'agent_id'],
  },

  {
    key: 'cost_coverage', // g360-k376
    rpc: 'cost_coverage',
    args: (t) => ({ p_tenant: t }),
    title: 'Cost coverage',
    description:
      'Per cost class: how many records, the quantity and its unit, the cost, and how many '
      + 'records could be priced against how many could not, with the rate basis used.',
    examples: [
      'how much of our AI cost is actually priced',
      'what cost do we have no rate for',
    ],
    value: ['cost_class', 'records', 'quantity', 'pricing_unit', 'cost_usd',
            'priced_records', 'rate_basis', 'note'],
    // Unpriced records are not zero cost. They are cost with no rate.
    unmeasured: ['unpriced_records'],
  },

  {
    key: 'behavior_coverage',
    rpc: 'agent_behavior_coverage',
    args: (t, p) => ({ p_tenant: t, p_window_days: (p && p.window_days) || 30 }),
    title: 'Agent behaviour coverage',
    description:
      'How many registered agents actually report behaviour in a window, the coverage percentage, '
      + 'and the agents that reported nothing.',
    examples: [
      'how many agents report behaviour',
      'what behavioural coverage do we have',
    ],
    value: ['registered_agents', 'reporting_agents', 'coverage_pct'],
    unmeasured: ['unreported_agents'],
    params: { window_days: { type: 'integer', default: 30 } },
  },

  {
    /* G360_K505 - machine-identity credentials, the gap named against
       Akto: per agent, what is known about the secrets and certificates
       on the Entra identity it authenticates as. Metadata only - dates,
       never a value. The same function Agent Identity renders (kit 494).
       Tenant-explicit overload; the function asserts access itself. */
    key: 'credential_status',
    rpc: 'nhi_credential_status',
    // The window is what "expiring" means. Held to 1..365 days: the model
    // supplies it, and a number it made up must not become a query.
    args: (t, p) => {
      const w = Math.round(Number(p && p.window_days));
      return { p_tenant: t, p_window_days: w >= 1 && w <= 365 ? w : 30 };
    },
    // One row per agent. Read a page at a time, as Agent Identity does, so a
    // count is over every agent and not over the first page the server sends.
    paged: true,
    title: 'Agent credential status',
    description:
      'Per agent: the Entra identity it authenticates as, and the state of that identity\'s secrets and '
      + 'certificates - expired, expiring within the window, current, none found in the directory, or '
      + 'not measured - with the soonest expiry, days left, how many credentials and how many expired, '
      + 'and when it was last read from Entra.',
    examples: [
      'which agents have expired credentials',
      'which agent credentials expire this month',
      'how many agents have a credential we have not checked',
      'do our agents have secrets in our directory',
    ],
    value: ['agent_name', 'entra_id_kind', 'status', 'soonest_expiry', 'days_left',
            'credentials', 'expired_count', 'last_synced'],
    detail: ['agent_id', 'entra_object_id'],
    params: { window_days: { type: 'integer', default: 30 } },
    caveat: 'Five answers are different facts. expired / expiring / current were read from Entra. '
      + 'none_found means Entra was asked and returned none - for Copilot Studio and Power Platform agents on '
      + 'Microsoft-managed service principals that is expected: the publisher holds the credential. '
      + 'not_measured means nobody asked (no app identity, never read, or the read failed) - say unknown, '
      + 'never clean, and never count it as zero expired. An agent with no Entra identity has no credential '
      + 'of its own to measure. Entra agent identities are not read yet.',
    /* Named counts (see metricAnswer.js). A count that could read as
       reassuring carries not_measured beside it: "0 expired" said alone,
       over an estate where most agents were never asked, is the clean bill
       this product must not give. */
    counts: {
      expired:      { label: 'agents whose identity has an expired secret or certificate',
                      test: (r) => r.status === 'expired',
                      alongside: ['not_measured'] },
      expiring:     { label: 'agents whose identity has a secret or certificate expiring within the window',
                      test: (r) => r.status === 'expiring',
                      alongside: ['not_measured'] },
      current:      { label: 'agents whose identity credentials were read and are all current',
                      test: (r) => r.status === 'current',
                      alongside: ['not_measured'] },
      none_found:   { label: 'agents whose identity was read from Entra and holds no secret or certificate there',
                      test: (r) => r.status === 'none_found' },
      not_measured: { label: 'agents whose credentials were not measured - unknown, not clean',
                      test: (r) => !CRED_READ[r.status] },
      no_identity:  { label: 'agents with no Entra identity, so no credential of their own to measure',
                      test: (r) => !r.entra_object_id },
      unread_identity: { label: 'agents that have an Entra identity whose credentials have not been read',
                      test: (r) => !!r.entra_object_id && !CRED_READ[r.status] },
    },
  },

  {
    /* G360_K506 - OWASP Top 10 for LLM Applications, the same shape as
       asi_coverage and the same function the LLM Top 10 page renders. */
    key: 'llm_top10',
    rpc: 'llm_top10_coverage',
    args: (t) => ({ p_tenant: t }),
    title: 'OWASP Top 10 for LLM Applications',
    description:
      'Per risk code LLM01 to LLM10: whether Govern360 observes it, partly observes it or cannot observe it, '
      + 'what it sees, the limit of what it sees, and a count with a label saying what was counted. '
      + 'Assessed from configuration - no agent is sent a prompt or tested.',
    examples: [
      'are we covered for the OWASP LLM top 10',
      'which LLM top 10 risks can Govern360 not see',
      'how many agents are exposed to prompt injection',
      'do we have excessive agency',
    ],
    value: ['code', 'risk_name', 'status', 'finding_count', 'finding_label'],
    unmeasured: ['the_limit'],
    detail: ['what_we_see'],
    alwaysWith: ['the_limit'],
    caveat: 'Assessed from configuration, never red teaming: nothing was attacked, so never say an agent '
      + 'withstood or resisted anything. status covered is shown on the page as Observed - say observed, never '
      + 'covered, compliant or protected. not_observed means Govern360 has no visibility, not that the risk is '
      + 'absent. finding_count means what finding_label says: for LLM10 it counts budgets enabled, a control, '
      + 'not a problem.',
    counts: {
      not_observed: { label: 'LLM Top 10 risks Govern360 cannot observe', test: (r) => r.status === 'not_observed' },
      partial:      { label: 'LLM Top 10 risks Govern360 partly observes', test: (r) => r.status === 'partial' },
      observed:     { label: 'LLM Top 10 risks Govern360 observes from configuration', test: (r) => r.status === 'covered' },
    },
  },

  {
    /* G360_K506 - agents whose creator has left; the Orphaned Agents card.
       Departed and unresolved are two facts and stay two counts. */
    key: 'orphaned_agents',
    rpc: 'orphaned_agents',
    args: (t) => ({ p_tenant: t }),
    title: 'Agents whose creator has left',
    description:
      'Agents whose creator is departed (directory account disabled) or unresolved (creator not in the synced '
      + 'directory), with autonomy tier, whether anyone was assigned as owner, whether a purpose was ever '
      + 'declared, and the agent status.',
    examples: [
      'which agents were built by people who have left',
      'how many orphaned agents do we have',
      'do any orphaned agents act on their own',
      'which agents did nobody inherit',
    ],
    value: ['agent_name', 'environment_name', 'autonomy_tier', 'creator_name', 'creator_state',
            'has_assigned_owner', 'has_purpose', 'agent_status'],
    // The creator's account name stays in the drill-down.
    detail: ['agent_id', 'creator_upn'],
    caveat: 'Two states, never merged. departed means the creator account is disabled. unresolved means the '
      + 'creator is not in the synced directory - a departure, a service principal, or a user the sync has not '
      + 'covered - so never call an unresolved agent orphaned or add it to the departed number. The agent keeps '
      + 'running on its own credentials after its creator is disabled.',
    emptyText: 'No agent was found whose creator is disabled or missing from the synced directory. That covers '
      + 'the directory Govern360 syncs, not accounts outside it.',
    counts: {
      departed:           { label: 'agents whose creator account is disabled',
                            test: (r) => r.creator_state === 'departed', alongside: ['unresolved'] },
      unresolved:         { label: 'agents whose creator is not in the synced directory - not counted as departed',
                            test: (r) => r.creator_state !== 'departed' },
      departed_acting:    { label: 'agents with a disabled creator that act at A3 or A4 autonomy',
                            test: (r) => r.creator_state === 'departed' && ACTING(r) },
      departed_no_owner:  { label: 'agents with a disabled creator that nobody was assigned to own',
                            test: (r) => r.creator_state === 'departed' && !r.has_assigned_owner },
      departed_no_purpose: { label: 'agents with a disabled creator whose purpose was never declared',
                            test: (r) => r.creator_state === 'departed' && !r.has_purpose },
    },
  },

  {
    /* G360_K506 - how much token spend carries an owner on one dimension. */
    key: 'token_attribution',
    rpc: 'token_attribution_coverage',
    args: (t, p) => {
      const d = String((p && p.dimension) || '');
      const args = { p_tenant: t };
      if (ATTRIBUTION_DIMS[d]) args.p_dimension = d;
      return withMonth(args, p);
    },
    title: 'Token spend attribution',
    description:
      'For one calendar month (this month unless one is named): total token cost, how much of it is attributed '
      + 'to a cost centre (or business unit, team, environment, agent or app), how much is not, the attributed '
      + 'percentage, and the same for request counts.',
    examples: [
      'how much of our AI spend is attributed',
      'how much token cost has no cost centre',
      'what share of spend is attributed to an agent',
    ],
    value: ['total_cost', 'attributed_cost', 'unattributed_cost', 'attributed_pct',
            'total_requests', 'attributed_requests'],
    alwaysWith: ['total_requests'],
    params: {
      month: { type: 'string', format: 'YYYY-MM', default: 'this month' },
      dimension: { type: 'string', one_of: Object.keys(ATTRIBUTION_DIMS), default: 'cost_center' },
    },
    caveat: 'One calendar month, to date if it is this month. Attributed means the request carries a value for '
      + 'the chosen dimension, not that the value is right. Cost is the estimated cost stored on each request; '
      + 'a model with no rate is stored at zero and is missing from these figures - see pricing coverage.',
    isEmpty: (rows) => !rows.length || !Number(rows[0].total_requests),
    emptyText: 'No token usage was recorded for that month, so there is no attribution to report - not 0 per cent '
      + 'and not 100 per cent.',
  },

  {
    /* G360_K506 - which models' spend has a rate behind it. */
    key: 'token_pricing',
    rpc: 'token_pricing_coverage',
    args: (t, p) => withMonth({ p_tenant: t }, p),
    title: 'Token pricing coverage',
    description:
      'Per provider and model for one calendar month (this month unless one is named): requests, tokens, the '
      + 'cost stored, whether a rate exists, what the tokens would cost at that rate, and a classification - '
      + 'priced, unpriced, priced but stored at zero, or a non-model cost line.',
    examples: [
      'which models have no price',
      'is any AI spend missing a rate',
      'how many models are unpriced',
    ],
    value: ['provider', 'model', 'events', 'total_tokens', 'stored_cost', 'recostable_cost', 'classification'],
    flag: ['has_price'],
    params: { month: { type: 'string', format: 'YYYY-MM', default: 'this month' } },
    caveat: 'Where has_price is false the model has no rate: its stored cost is not what it cost, and its '
      + 'recostable_cost of 0 means no rate, never free. Never total stored_cost as AI spend while any model is '
      + 'unpriced without saying so.',
    emptyText: 'No token usage was recorded for that month, so there is nothing to price.',
    counts: {
      unpriced:        { label: 'models used this month with no rate', test: (r) => r.has_price === false
                           && !/^non-model/.test(String(r.classification || '')) },
      stored_at_zero:  { label: 'models with a rate whose usage was stored at zero cost',
                         test: (r) => /^priced but stored at zero/.test(String(r.classification || '')) },
      priced:          { label: 'models priced and stored with a cost', test: (r) => r.classification === 'priced' },
      non_model:       { label: 'cost-import lines that are not a model', test: (r) => /^non-model/.test(String(r.classification || '')) },
    },
  },

  {
    /* G360_K507 - "what do I fix first": the findings the Exposure Score
       page lists under What to fix. They arrive inside ai_exposure_score's
       row, so the entry calls that (tenant-explicit, confirmed by recon
       507) and answers from its findings array - the same list the page
       shows, not a second computation of it. */
    key: 'exposure_findings',
    rpc: 'ai_exposure_score',
    args: (t) => ({ p_tenant: t }),
    pick: (rows) => { const f = rows[0] && rows[0].findings; return Array.isArray(f) ? f : []; },
    title: 'What to fix to raise the exposure score',
    description:
      'The findings behind the AI Exposure Score, in the order the page lists them: the dimension, severity, '
      + 'what it is losing, the evidence, the remedy, the points fixing it is expected to gain, and the effort.',
    examples: [
      'what would raise our exposure score',
      'how many points can we gain',
      'what is each dimension losing',
      'what is the quickest improvement to the score',
    ],
    value: ['dimension', 'severity', 'cause', 'evidence', 'remedy', 'expected_gain', 'effort'],
    caveat: 'expected_gain is a modelled estimate of score points, at most 8 per finding. 0 or missing means no '
      + 'point value was modelled - never that fixing it is worth nothing. A dimension with no connected '
      + 'signal has points unearned, not lost; its remedy is to connect a source. Effort may be missing; do '
      + 'not guess it. Never add the gains up into a new score.',
    emptyText: 'No open findings - all measured dimensions are strong.',
    counts: {
      high: { label: 'findings of high or critical severity',
              test: (r) => /^(high|critical)$/i.test(String(r.severity || '')) },
    },
  },
];

export const METRIC_BY_KEY = METRICS.reduce((m, e) => { m[e.key] = e; return m; }, {});

/* The router sees only this - names, descriptions and examples. Never the
   schema, never the SQL. It selects a key and supplies parameters; anything
   it cannot place is a refusal, and a refusal is a logged product signal
   rather than a guess. */
export function routerCatalogue() {
  return METRICS.map((m) => ({
    key: m.key,
    title: m.title,
    description: m.description,
    examples: m.examples,
    params: m.params || null,
    counts: m.counts ? Object.keys(m.counts) : null, // G360_K505
  }));
}

/* G360_K506 - what is registered and what is not.

   Registered since this list was first written: exposure paths, legs and
   signal and cost_coverage (kit 376), credential_status (kit 505),
   llm_top10, orphaned_agents, token_attribution and token_pricing (kit
   506).

   G360_K507 - exposure_findings registered, from ai_exposure_score's own
   findings array. Recon 507 confirmed ai_exposure_score(p_tenant uuid)
   exists and asserts tenant access, so exposure_score above was already
   calling the right overload.
*/
